import { Injectable, inject } from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {SocketService} from '@app/services/socket.service';

/** Requester types whose socket messages are backed by a task_log row. */
const TASK_REQUESTER_TYPES = ['influencer', 'influencer_sync', 'influencer_bulk_operations', 'data_protection'];
const DONE_STATUSES = ['finished', 'failed'];
const SEEN_STORAGE_KEY = 'taskNotificationSeenAt';

/**
 * Backs the task notification icon in the panel header.
 *
 * "Relevant" tasks are the ones the user started themselves plus the ones running on the project
 * they currently have open — the same scope the API applies. The summary endpoint gives the
 * authoritative state on load / project change, socket messages keep it live in between.
 */
@Injectable({
  providedIn: 'root'
})
export class TaskNotificationService {
  private http = inject(HttpClient);
  private socketService = inject(SocketService);

  private summaryUrl = environment.apiUrl + '/api/v1/task/summary';

  private runningSource = new BehaviorSubject<number>(0);
  private lastDoneAtSource = new BehaviorSubject<number>(0);
  private seenAtSource = new BehaviorSubject<number>(0);
  private refreshTrigger = new Subject<void>();
  // The getters below read these, so a view that shows them refreshes when a socket message or
  // the summary changes them, without zone.js.
  private readonly running = toSignal(this.runningSource, {requireSync: true});
  private readonly lastDoneAt = toSignal(this.lastDoneAtSource, {requireSync: true});
  private readonly seenAt = toSignal(this.seenAtSource, {requireSync: true});

  private projectId: number | string = null;
  private userId: number | string = null;
  private enabled = false;

  constructor() {
    // Terminal socket messages arrive in bursts (finish + a trailing stay); coalesce the
    // authoritative re-read so a finishing task does not fire several requests
    this.refreshTrigger.pipe(debounceTime(400)).subscribe(() => this.loadSummary());
    this.socketService.sourceData.subscribe((message: any) => this.handleSocketMessage(message));
  }

  /** Project the user currently has open, or null. */
  get openProjectId(): number | string {
    return this.projectId;
  }

  /** True while at least one relevant task is still working. */
  get hasRunning(): boolean {
    return this.running() > 0;
  }

  /** True when a relevant task finished that the user has not acknowledged yet. */
  get hasUnseenDone(): boolean {
    return this.lastDoneAt() > this.seenAt();
  }

  get runningCount(): number {
    return this.running();
  }

  /**
   * Starts tracking once a user is logged in. Safe to call repeatedly.
   * The seen state is stored per user, so on a shared browser one account's
   * acknowledgement never hides another account's badge.
   */
  start(userId: number | string = null) {
    if (this.enabled && this.userId === (userId || null)) {
      return;
    }
    this.userId = userId || null;
    this.enabled = true;
    this.seenAtSource.next(this.readSeenAt());
    this.loadSummary();
  }

  stop() {
    this.enabled = false;
    this.runningSource.next(0);
    this.lastDoneAtSource.next(0);
  }

  /** Called by the shell whenever the open project changes (null outside a project). */
  setProjectId(projectId: number | string) {
    const next = projectId || null;
    if (this.projectId === next) {
      return;
    }
    this.projectId = next;
    if (this.enabled) {
      this.loadSummary();
    }
  }

  /**
   * Feeds a socket message in. Returns true when the message was a task update.
   */
  handleSocketMessage(message: any): boolean {
    if (!this.enabled) {
      return false;
    }
    const data = message && message.data;
    if (!data || !TASK_REQUESTER_TYPES.includes(data.requesterType)) {
      return false;
    }
    // Of the bulk operations only a delete has a task_log row, and its messages carry it
    if (data.requesterType === 'influencer_bulk_operations' && !(data.result && data.result.id)) {
      return false;
    }

    const status = data.result && data.result.status ? data.result.status : data.status;
    if (DONE_STATUSES.includes(status)) {
      this.lastDoneAtSource.next(Date.now());
    } else if (!this.hasRunning) {
      // Show the icon as active straight away, the refresh below confirms the real count
      this.runningSource.next(1);
    }
    this.refreshTrigger.next();
    return true;
  }

  /** Marks every completed task as acknowledged, clearing the badge. */
  markSeen() {
    const now = Date.now();
    try {
      localStorage.setItem(this.storageKey(), String(now));
    } catch (e) {
      // private mode / storage disabled — badge simply will not survive a reload
    }
    this.seenAtSource.next(now);
  }

  /** Re-reads the authoritative counters. */
  loadSummary() {
    if (!this.enabled) {
      return;
    }
    const params: any = {};
    if (this.projectId) {
      params.project_id = this.projectId;
    }
    this.http.get(this.summaryUrl, {params}).subscribe((resp: any) => {
      if (!resp || !resp.success || !resp.data) {
        return;
      }
      this.runningSource.next(Number(resp.data.running) || 0);
      const lastDoneAt = resp.data.last_done_at ? new Date(resp.data.last_done_at).getTime() : 0;
      if (lastDoneAt > this.lastDoneAtSource.value) {
        this.lastDoneAtSource.next(lastDoneAt);
      }
    }, () => {
      // leave the last known state on a failed read
    });
  }

  private storageKey(): string {
    return this.userId ? `${SEEN_STORAGE_KEY}:${this.userId}` : SEEN_STORAGE_KEY;
  }

  private readSeenAt(): number {
    try {
      return Number(localStorage.getItem(this.storageKey())) || 0;
    } catch (e) {
      return 0;
    }
  }
}
